import { faAddressBook } from '@fortawesome/free-solid-svg-icons';

/**
 * CRM Partner Management walkthrough.
 *
 * Each step targets a stable ``data-tour`` selector in the live UI. The tour
 * spans two routes:
 *   - /dashboard/crm                       (steps 1-7)
 *   - /dashboard/crm/profile/new           (steps 8-11)
 *
 * Step shape (in addition to standard react-joyride fields):
 *   route          - optional route the step belongs to. Defaults to the
 *                    tour's ``startRoute``. The engine navigates here via
 *                    react-router before showing the step.
 *   skipIfMissing  - if true and the target is not in the DOM at tour start,
 *                    the engine drops the step. Used for elements that only
 *                    render under certain permissions (e.g., Import Partners
 *                    is only visible to CRM approvers).
 *   disableBeacon  - mapped to v3's ``skipBeacon`` by the engine.
 *   isLast         - documentation-only marker for the wrap-up step.
 */

const CRM_ROUTE = '/dashboard/crm';
const CRM_NEW_PROFILE_ROUTE = '/dashboard/crm/profile/new';

const crmTour = {
    id: 'crm',
    title: 'Partner Management (CRM)',
    description:
        'Find partners, read risk and approval signals, then walk through creating a brand new partner from scratch.',
    estimatedMinutes: 5,
    startRoute: CRM_ROUTE,
    icon: faAddressBook,
    group: 'module',
    steps: [
        {
            target: '[data-tour="crm-page"]',
            title: 'Welcome to Partner Management',
            content:
                'This is your CRM dashboard. Every counterparty you trade with - debtors, creditors, IMT group entities - lives here with a full compliance and trading record.',
            placement: 'center',
            disableBeacon: true,
        },
        {
            target: '[data-tour="crm-stats"]',
            title: 'Partner stats at a glance',
            content:
                'These cards summarize partners by approval state, partners that need your attention, archived records, and IMT group entities. They update live as data changes.',
            placement: 'right',
        },
        {
            target: '[data-tour="crm-search"]',
            title: 'Search partners',
            content:
                'Type any part of a partner name to filter the list. Search runs on the server, so it works across all partners - not just the visible page.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="crm-tabs"]',
            title: 'Filter by status',
            content:
                'Switch between All, Approved, Pending, Rejected, Needs Attention, IMT Entities, and Archived. The active tab controls which partners appear in the list.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="crm-profiles-grid"]',
            title: 'Partner cards',
            content:
                'Each card shows the legal name, partner type, primary contact, last transaction, risk rating, AML level and sanction status. The colored badge in the top-right indicates approval status. Click any card to open the full profile.',
            placement: 'bottom',
        },
        {
            // Conditionally rendered: only CRM approvers see the Import
            // Partners button. The engine drops this step automatically for
            // users who do not have the button in their DOM.
            target: '[data-tour="crm-import-partners"]',
            title: 'Bulk-import partners',
            content:
                'CRM approvers can import many partners at once via CSV. The template includes legal name, partner type, address, banking details, credit limit and risk rating. Each row becomes a new partner profile.',
            placement: 'bottom',
            skipIfMissing: true,
        },
        {
            target: '[data-tour="crm-add-new"]',
            title: 'Add a new partner',
            content:
                'Click Next and we\'ll open the new partner form so you can see how to create one from scratch.',
            placement: 'bottom',
        },
        {
            // First step on the new-profile route. The engine navigates via
            // react-router before showing this step; targetWaitTimeout in
            // the engine options gives React time to mount the destination.
            route: CRM_NEW_PROFILE_ROUTE,
            target: '[data-tour="crm-profile-tabs"]',
            title: 'Partner profile sections',
            content:
                'A partner profile is divided into tabs: Partner Profile (basic details), Banking, Contacts, Commodity, KYC, Credit Rating, Documents, and more. We\'re starting on Partner Profile - the minimum required data lives here.',
            placement: 'bottom',
        },
        {
            route: CRM_NEW_PROFILE_ROUTE,
            target: '[data-tour="crm-profile-name"]',
            title: 'Legal Entity Name',
            content:
                'This is the only strictly required field to create the partner. As you type, the system suggests possible duplicate matches so you don\'t accidentally create the same entity twice. Other details (trading name, address, partner type) can be filled in now or later.',
            placement: 'right',
        },
        {
            route: CRM_NEW_PROFILE_ROUTE,
            target: '[data-tour="crm-profile-save"]',
            title: 'Save the partner',
            content:
                'Click Save Profile to create the partner. The system automatically runs an AML and sanctions screening on the legal entity name. After saving, the other tabs unlock and you can fill in banking, contacts, KYC documents and credit rating.',
            placement: 'left',
        },
        {
            route: CRM_NEW_PROFILE_ROUTE,
            target: 'body',
            title: 'You are good to go',
            content:
                'That is the CRM tour. From any partner profile you can manage KYC, banking, contacts, credit ratings, and trading history. Re-run this tour any time from Hamster Training.',
            placement: 'center',
            disableBeacon: true,
            isLast: true,
        },
    ],
};

export default crmTour;
